import React, { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';

export type QuietConfirmTone = 'default' | 'danger';

export interface QuietConfirmProps {
  isOpen: boolean;
  title: string;
  detail?: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: QuietConfirmTone;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Small in-app confirm. No browser dialog. No large action buttons.
 */
const QuietConfirm: React.FC<QuietConfirmProps> = ({
  isOpen,
  title,
  detail,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  loading = false,
  onConfirm,
  onClose,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => confirmRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[110]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-900/25 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Dialog.Panel className="w-[min(19rem,calc(100vw-2rem))] rounded-xl border border-primary-500/10 bg-white p-4 text-left shadow-modal">
                <Dialog.Title className="text-sm font-semibold text-primary-800">
                  {title}
                </Dialog.Title>
                {detail ? (
                  <p className="mt-0.5 truncate text-xs text-primary-600">{detail}</p>
                ) : null}
                <p className="mt-2 text-xs leading-relaxed text-neutral-600">{message}</p>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="rounded px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 hover:text-primary-800 disabled:opacity-50"
                  >
                    {cancelLabel}
                  </button>
                  <button
                    ref={confirmRef}
                    type="button"
                    onClick={onConfirm}
                    disabled={loading}
                    className={
                      tone === 'danger'
                        ? 'rounded px-2.5 py-1 text-xs font-medium text-error-700 hover:bg-error-50 disabled:opacity-50'
                        : 'rounded bg-primary-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-600 disabled:opacity-50'
                    }
                  >
                    {loading ? 'Working…' : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QuietConfirm;
